import React from "react";
import WebProfitSpinner from "../../components/system/WebProfitSpinner";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Checkbox,
  Collapse,
  Divider,
  Grid,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import { useNavigate } from "react-router-dom";
import { landingAssetsApi } from "../../api/landingAssets";
import { projectsApi } from "../../api/projects";
import { settingsApi } from "../../api/settings";
import PendingAssetField from "../../components/dashboard/landingEditor/PendingAssetField";
import ContextualHelp from "../../components/dashboard/ContextualHelp";
import ProjectDeploymentCard from "../../components/dashboard/ProjectDeploymentCard";
import DashboardLoadingState from "../../components/dashboard/DashboardLoadingState";
import { useAuth } from "../../context/AuthContext";
import { useProjects } from "../../context/ProjectsContext";
import { assetValueFromResponse } from "../../utils/landingAssets";
import { resolveProjectNavigation } from "../../utils/dashboardNavigation";
import { hasProjectRole, normalizeProjectAccess } from "../../utils/projectAccess";
import { isSuperAdmin } from "../../utils/roles";
import {
  PROJECT_BUSINESS_PROFILES,
  capabilitiesForBusinessGoals,
} from "../../utils/projectCapabilities";
import {
  activeModuleLabels,
  BILLING_PLAN_LABELS,
  modulesForPlan,
  normalizeProjectModules,
  normalizeProjectPlanCode,
} from "../../utils/projectModules";

const BRAND = {
  orange: "#F35A3A",
  dark: "var(--wp-panel-text-primary, #19242F)",
  purple: "#6957E8",
  yellow: "#FFC857",
  green: "#26A269",
  blue: "#2F80ED",
};
const emptyBranding = { logo: null, compactLogo: null, favicon: null };

function emptyForm(email = "", plan = "catalogo") {
  return {
    name: "",
    slug: "",
    ownerEmail: email,
    plan,
    businessProfile: "",
    businessGoals: [],
    domains: "",
  };
}

function makeSlug(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

async function uploadInitialBranding(projectId, branding, businessName) {
  const uploads = [
    {
      file: branding.logo,
      field: "logoUrl",
      sectionKey: "branding",
      assetKey: "logo",
    },
    {
      file: branding.compactLogo,
      field: "compactLogoUrl",
      sectionKey: "branding",
      assetKey: "compact-logo",
    },
    {
      file: branding.favicon,
      field: "faviconUrl",
      sectionKey: "branding",
      assetKey: "favicon",
    },
  ].filter((item) => item.file);

  if (!uploads.length) return;

  const uploadedPaths = [];
  const theme = {};

  try {
    for (const item of uploads) {
      const response = await landingAssetsApi.upload(projectId, item.file, {
        pageKey: "home",
        sectionKey: item.sectionKey,
        assetKey: item.assetKey,
      });
      const value = assetValueFromResponse(response);
      if (!value) throw new Error("El backend no devolvió la ruta del archivo");
      uploadedPaths.push(value);
      theme[item.field] = value;
    }

    await settingsApi.update(projectId, {
      business: { name: businessName },
      seo: { title: businessName },
      theme,
    });
  } catch (error) {
    await Promise.all(
      uploadedPaths.map((path) => landingAssetsApi.remove(projectId, path).catch(() => null))
    );
    throw error;
  }
}

const FIRST_PROJECT_BINARY_STEPS = Object.freeze([
  {
    id: "products",
    answerKey: "products",
    title: "¿Vendés productos?",
    help: "Esto prioriza catálogo, pedidos y herramientas de venta.",
    detail: "Elegí Sí si cargás artículos, mercadería o cualquier producto con precio. Podés cambiar esta decisión después.",
  },
  {
    id: "services",
    answerKey: "services",
    title: "¿Ofrecés servicios?",
    help: "Esto prioriza servicios, agenda y seguimiento de la atención.",
    detail: "Incluye turnos, trabajos, clases, consultas u otros servicios que tu negocio presta a clientes.",
  },
  {
    id: "customers",
    answerKey: "customers",
    title: "¿Querés gestionar clientes?",
    help: "Activa el recorrido inicial hacia CRM y seguimiento de clientes.",
    detail: "Sirve si necesitás guardar clientes, datos de contacto, historial o acciones comerciales desde WebProfit.",
  },
  {
    id: "memberships",
    answerKey: "memberships",
    title: "¿Usás membresías o planes?",
    help: "Esto prepara el circuito para asignar planes a clientes.",
    detail: "Es útil para gimnasios, academias, clubes o negocios que cobran abonos y renovaciones periódicas.",
  },
  {
    id: "menu",
    answerKey: "menu",
    title: "¿Necesitás una carta?",
    help: "Esto habilita la carta gastronómica sobre tus mismos ítems.",
    detail: "Elegí Sí si querés publicar platos, bebidas o productos organizados como una carta para tus clientes.",
  },
  {
    id: "tables",
    answerKey: "tables",
    title: "¿Trabajás con mesas?",
    help: "Esto prepara mesas y reservas de mesa para la operación gastronómica.",
    detail: "Elegí Sí si necesitás organizar un salón, asignar mesas o recibir reservas vinculadas a mesas.",
  },
  {
    id: "management",
    answerKey: "management",
    title: "¿Querés controlar la gestión?",
    help: "Esto prioriza sistema interno, métricas y control operativo.",
    detail: "Elegí Sí si además de vender o atender querés seguir la operación y revisar indicadores del negocio.",
  },
]);

const REAL_ESTATE_PROJECT_BINARY_STEPS = Object.freeze([
  {
    id: "properties",
    answerKey: "properties",
    title: "¿Querés gestionar propiedades?",
    help: "Prepara el contrato para organizar el inventario inmobiliario.",
    detail: "Incluye propiedades disponibles, datos comerciales y futura operación inmobiliaria sin mezclarla con el catálogo común.",
  },
  {
    id: "leases",
    answerKey: "leases",
    title: "¿Gestionás contratos o alquileres?",
    help: "Prepara el seguimiento de contratos como capacidad independiente.",
    detail: "Elegí Sí si necesitás administrar alquileres y vincularlos con propiedades y clientes.",
  },
  {
    id: "leaseCollections",
    answerKey: "leaseCollections",
    title: "¿Querés gestionar cobranzas de alquileres?",
    help: "Prepara una capacidad de cobranzas separada del resto de la gestión.",
    detail: "Permite asignar permisos específicos de cobranza sin habilitar páginas inmobiliarias todavía.",
  },
]);

const REAL_ESTATE_GUIDED_ANSWER_KEYS = Object.freeze(
  REAL_ESTATE_PROJECT_BINARY_STEPS.map(({ answerKey }) => answerKey),
);

function guidedAnswersForProfile(businessProfile, answers = {}) {
  if (businessProfile === "real_estate") return answers;

  return {
    ...answers,
    ...Object.fromEntries(REAL_ESTATE_GUIDED_ANSWER_KEYS.map((answerKey) => [answerKey, null])),
  };
}

function guidedBusinessGoals(businessProfile, answers = {}) {
  const applicableAnswers = guidedAnswersForProfile(businessProfile, answers);
  const goals = [];
  if (applicableAnswers.products === true) goals.push("sellProducts");
  if (applicableAnswers.services === true) goals.push("offerServices");
  if (applicableAnswers.customers === true) goals.push("manageCustomers");
  if (applicableAnswers.memberships === true) goals.push("manageMemberships");
  if (applicableAnswers.menu === true) goals.push("gastronomicMenu");
  if (applicableAnswers.tables === true) goals.push("takeReservations");
  if (applicableAnswers.management === true) goals.push("controlBusiness");
  if (applicableAnswers.properties === true) goals.push("manageProperties");
  if (applicableAnswers.leases === true) goals.push("manageLeases");
  if (applicableAnswers.leaseCollections === true) goals.push("manageLeaseCollections");
  return goals;
}

function guidedCapabilities(businessProfile, answers = {}) {
  const applicableAnswers = guidedAnswersForProfile(businessProfile, answers);
  const goals = guidedBusinessGoals(businessProfile, applicableAnswers);
  const capabilities = capabilitiesForBusinessGoals(businessProfile, goals);

  return {
    ...capabilities,
    memberships: applicableAnswers.memberships === true,
    gastronomicMenu: applicableAnswers.menu === true,
    tables: applicableAnswers.tables === true,
    tableReservations: applicableAnswers.tables === true,
    properties: applicableAnswers.properties === true,
    leases: applicableAnswers.leases === true,
    leaseCollections: applicableAnswers.leaseCollections === true,
  };
}

function ProjectActionsMenu({
  project,
  superAdmin,
  showPlan,
  showLanding,
  showProducts,
  showOrders,
  onNavigate,
}) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const slug = project?.slug;
  const actions = [
    superAdmin
      ? { key: "manage", label: "Administrar", path: `/dashboard/proyectos/${slug}/configuracion/general` }
      : null,
    showPlan
      ? { key: "plan", label: "Plan y suscripción", path: `/dashboard/proyectos/${slug}/configuracion/suscripcion` }
      : null,
    showLanding
      ? { key: "landing", label: "Landing", path: `/dashboard/proyectos/${slug}/pagina` }
      : null,
    showProducts
      ? { key: "products", label: "Productos", path: `/dashboard/proyectos/${slug}/productos` }
      : null,
    showOrders
      ? { key: "orders", label: "Pedidos", path: `/dashboard/proyectos/${slug}/pedidos` }
      : null,
  ].filter(Boolean);

  if (!actions.length) return null;

  const go = (path) => {
    setAnchorEl(null);
    onNavigate(path);
  };

  return (
    <>
      <Button
        type="button"
        fullWidth
        variant="outlined"
        endIcon={<MoreHorizRoundedIcon />}
        aria-haspopup="menu"
        aria-expanded={open ? "true" : undefined}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          minHeight: 42,
          borderRadius: 2.2,
          textTransform: "none",
          fontWeight: 900,
          color: BRAND.dark,
          borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.14)",
        }}
      >
        Más acciones
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: .6,
            width: 260,
            maxWidth: "calc(100vw - 24px - env(safe-area-inset-left) - env(safe-area-inset-right))",
            maxHeight: "calc(100dvh - 96px - env(safe-area-inset-top) - env(safe-area-inset-bottom))",
            borderRadius: 2.5,
            border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)",
            boxShadow: "0 18px 46px rgba(var(--wp-panel-shadow-rgb, 25,36,47),.16)",
          },
        }}
        MenuListProps={{ "aria-label": `Acciones de ${project?.name || "proyecto"}` }}
      >
        {actions.map((action) => (
          <MenuItem
            key={action.key}
            onClick={() => go(action.path)}
            sx={{ minHeight: 44, fontSize: ".86rem", fontWeight: 850 }}
          >
            {action.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

// UX-2026 | The first project is activated from one screen.
// Only the business name is required. Existing guidedBusinessGoals and
// guidedCapabilities remain the *only* payload translators, so no backend/ERP
// module contract changes. Optional choices are explicitly opt-in.
const ONBOARDING_PROFILE_FEATURES = Object.freeze({
  commerce: ["products", "services", "customers", "management"],
  services: ["services", "customers", "memberships", "management"],
  gym: ["services", "customers", "memberships", "management"],
  restaurant: ["products", "services", "customers", "menu", "tables", "management"],
  real_estate: ["customers", "management", "properties", "leases", "leaseCollections"],
  default: ["products", "services", "customers", "memberships", "menu", "tables", "management"],
});
const ONBOARDING_PROFILE_SUGGESTIONS = Object.freeze({
  commerce: ["products", "customers"],
  services: ["services", "customers"],
  gym: ["services", "customers", "memberships"],
  restaurant: ["products", "menu", "tables"],
  real_estate: ["properties", "leases", "leaseCollections"],
});

function FirstProjectOnboarding({ form, creating, createStatus, error, onChange, onSubmit }) {
  const [showOptions, setShowOptions] = React.useState(false);
  const [answers, setAnswers] = React.useState(() => Object.fromEntries(
    [...FIRST_PROJECT_BINARY_STEPS, ...REAL_ESTATE_PROJECT_BINARY_STEPS].map(({ answerKey }) => [answerKey, null]),
  ));
  const generatedSlug = makeSlug(form.slug || form.name);
  const nameReady = Boolean(form.name.trim() && generatedSlug);
  const featureKeys = ONBOARDING_PROFILE_FEATURES[form.businessProfile] || ONBOARDING_PROFILE_FEATURES.default;
  const optionalFeatures = [...FIRST_PROJECT_BINARY_STEPS, ...REAL_ESTATE_PROJECT_BINARY_STEPS]
    .filter(({ answerKey }) => featureKeys.includes(answerKey));
  const activeOptions = optionalFeatures.filter(({ answerKey }) => answers[answerKey] === true).length;

  const selectProfile = (value) => {
    // Profile suggestions are UI defaults, not hardcoded server features.
    // Changing rubro resets only the *optional* draft selection.
    const recommended = new Set(ONBOARDING_PROFILE_SUGGESTIONS[value] || []);
    setAnswers(Object.fromEntries(
      [...FIRST_PROJECT_BINARY_STEPS, ...REAL_ESTATE_PROJECT_BINARY_STEPS]
        .map(({ answerKey }) => [answerKey, recommended.has(answerKey) ? true : null]),
    ));
    onChange({ target: { name: "businessProfile", value } });
  };

  const submit = (event) => {
    event.preventDefault();
    if (!nameReady || creating) return;
    onSubmit(event, {
      businessGoals: guidedBusinessGoals(form.businessProfile, answers),
      capabilities: guidedCapabilities(form.businessProfile, answers),
    });
  };

  return (
    <Box component="main" sx={{ maxWidth: 900, mx: "auto", pb: { xs: 2, md: 5 } }}>
      <Stack spacing={.75} sx={{ maxWidth: 700, mb: { xs: 2.2, sm: 3.2 } }}>
        <Typography sx={{ textTransform: "uppercase", color: "#F35A3A", fontSize: ".74rem", letterSpacing: ".12em", fontWeight: 900 }}>Tu primer paso en WebProfit</Typography>
        <Typography component="h1" sx={{ fontSize: { xs: "1.85rem", sm: "2.55rem" }, letterSpacing: "-.05em", lineHeight: 1.12, fontWeight: 900, color: BRAND.dark }}>
          Primero, creemos tu negocio.
        </Typography>
        <Typography sx={{ color: "var(--wp-panel-text-secondary, #5C6571)", fontSize: { xs: ".9rem", sm: "1rem" }, lineHeight: 1.6 }}>
          Solo necesitamos el nombre. Podés elegir tu rubro y personalizar las herramientas ahora o hacerlo más adelante.
        </Typography>
      </Stack>

      <Paper
        component="form"
        onSubmit={submit}
        elevation={0}
        sx={{ borderRadius: { xs: 3.3, md: 4 }, overflow: "hidden", border: "1px solid var(--wp-panel-border, rgba(25,36,47,.09))", bgcolor: "var(--wp-panel-surface, #fff)", boxShadow: "0 18px 54px rgba(0,0,0,.065)" }}
      >
        <Stack spacing={2.6} sx={{ p: { xs: 2, sm: 3.25, md: 4 } }}>
          {error ? <Alert severity="error" role="alert">{error}</Alert> : null}
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box sx={{ width: 44, height: 44, flexShrink: 0, borderRadius: 2.5, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.1)", color: "#E65133" }}><StorefrontRoundedIcon /></Box>
            <Box>
              <Typography component="h2" sx={{ fontWeight: 900, color: BRAND.dark, fontSize: "1.06rem" }}>Identidad del negocio</Typography>
              <Typography sx={{ color: "var(--wp-panel-text-secondary, #5C6571)", fontSize: ".8rem" }}>Lo básico para abrir tu espacio.</Typography>
            </Box>
          </Stack>
          <TextField
            label="Nombre de tu negocio"
            name="name"
            value={form.name}
            onChange={onChange}
            placeholder="Ej.: Mi comercio"
            autoComplete="organization"
            fullWidth
            required
            autoFocus
            disabled={creating}
            error={Boolean(form.name.trim() && !nameReady)}
            helperText={form.name.trim() && !nameReady ? "Ingresá un nombre con letras o números para generar la dirección web." : "Podés cambiar el nombre visible más adelante."}
            inputProps={{ maxLength: 90 }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.3 } }}
          />
          {nameReady ? (
            <Box sx={{ bgcolor: "var(--wp-panel-surface-soft, #F7F8FB)", px: 1.6, py: 1.3, borderRadius: 2.3, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <CheckCircleOutlineRoundedIcon sx={{ color: "#2E9D66", fontSize: 19 }} />
              <Typography sx={{ color: "var(--wp-panel-text-secondary, #5C6571)", fontSize: ".79rem" }}>Identificador inicial:</Typography>
              <Typography sx={{ color: BRAND.dark, fontWeight: 850, fontSize: ".83rem", overflowWrap: "anywhere" }}>{generatedSlug}</Typography>
            </Box>
          ) : null}
          <TextField
            select
            label="Rubro principal (opcional)"
            name="businessProfile"
            value={form.businessProfile}
            onChange={(event) => selectProfile(event.target.value)}
            helperText="Nos ayuda a sugerir herramientas. No limita lo que podés usar."
            fullWidth
            disabled={creating}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.3 } }}
          >
            <MenuItem value="">Elegir más adelante</MenuItem>
            {PROJECT_BUSINESS_PROFILES.map((profile) => <MenuItem key={profile.key} value={profile.key}>{profile.label}</MenuItem>)}
          </TextField>

          <Box sx={{ border: "1px solid var(--wp-panel-border, rgba(25,36,47,.10))", borderRadius: 2.7, overflow: "hidden" }}>
            <Button
              type="button"
              fullWidth
              onClick={() => setShowOptions((value) => !value)}
              disabled={creating}
              aria-expanded={showOptions}
              aria-controls="optional-first-project-features"
              endIcon={<KeyboardArrowDownRoundedIcon sx={{ transform: showOptions ? "rotate(180deg)" : "none", transition: "transform .18s ease" }} />}
              sx={{ minHeight: 64, px: { xs: 1.5, sm: 2 }, textTransform: "none", justifyContent: "space-between", color: BRAND.dark, textAlign: "left", "& .MuiButton-endIcon": { ml: 1 } }}
            >
              <Box>
                <Typography sx={{ fontWeight: 850, fontSize: ".92rem" }}>Personalizar herramientas (opcional)</Typography>
                <Typography sx={{ color: "var(--wp-panel-text-secondary, #5C6571)", fontSize: ".76rem", mt: .2 }}>
                  {activeOptions ? `${activeOptions} preferencias seleccionadas` : "Podés hacerlo después desde el panel"}
                </Typography>
              </Box>
            </Button>
            <Collapse in={showOptions} unmountOnExit={false}>
              <Box id="optional-first-project-features" sx={{ p: { xs: 1.4, sm: 2 }, borderTop: "1px solid var(--wp-panel-border, rgba(25,36,47,.10))", bgcolor: "var(--wp-panel-surface-soft, #F7F8FB)" }}>
                <Typography sx={{ color: "var(--wp-panel-text-secondary, #5C6571)", fontSize: ".79rem", lineHeight: 1.5, mb: 1.5 }}>
                  Marcá lo que necesitás priorizar. Son preferencias iniciales; no cambian tu período de prueba ni reemplazan la configuración de módulos.
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))" }, gap: 1 }}>
                  {optionalFeatures.map((feature) => (
                    <Box
                      component="label"
                      key={feature.answerKey}
                      sx={{ display: "flex", gap: .55, alignItems: "flex-start", border: "1px solid var(--wp-panel-border, rgba(25,36,47,.1))", borderRadius: 2, px: 1, py: .9, bgcolor: "var(--wp-panel-surface, #fff)", cursor: "pointer" }}
                    >
                      <Checkbox
                        checked={answers[feature.answerKey] === true}
                        disabled={creating}
                        onChange={(event) => setAnswers((previous) => ({ ...previous, [feature.answerKey]: event.target.checked }))}
                        inputProps={{ "aria-label": feature.title }}
                        sx={{ p: .55, color: "#F35A3A", "&.Mui-checked": { color: "#F35A3A" } }}
                      />
                      <Box sx={{ pt: .18 }}>
                        <Typography sx={{ fontSize: ".82rem", fontWeight: 850, color: BRAND.dark, lineHeight: 1.3 }}>{feature.title.replace(/[¿?]/g, "")}</Typography>
                        <Typography sx={{ color: "var(--wp-panel-text-secondary, #5C6571)", fontSize: ".72rem", lineHeight: 1.45, mt: .3 }}>{feature.help}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Collapse>
          </Box>
        </Stack>
        <Box sx={{ bgcolor: "var(--wp-panel-surface-soft, #F7F8FB)", borderTop: "1px solid var(--wp-panel-border, rgba(25,36,47,.09))", px: { xs: 2, sm: 3.25, md: 4 }, py: 2.1 }}>
          <Stack spacing={1.5}>
            {createStatus ? <Alert severity="info" role="status">{createStatus}</Alert> : null}
            <Typography sx={{ color: "var(--wp-panel-text-secondary, #5C6571)", fontSize: ".78rem", lineHeight: 1.5 }}>
              Empezás con las herramientas principales en prueba durante 30 días. Más adelante elegís cuáles mantener.
            </Typography>
            <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ sm: "center" }}>
              <Typography sx={{ maxWidth: 350, color: "var(--wp-panel-text-secondary, #5C6571)", fontSize: ".76rem", lineHeight: 1.5 }}>
                La creación incluye el espacio del negocio. Después podrás editar tu web y completar la configuración.
              </Typography>
              <Button
                type="submit"
                variant="contained"
                disabled={!nameReady || creating}
                startIcon={creating ? <WebProfitSpinner size={17} /> : undefined}
                endIcon={creating ? undefined : <ArrowForwardRoundedIcon />}
                sx={{ minHeight: 52, minWidth: { sm: 220 }, borderRadius: 2.5, textTransform: "none", fontWeight: 900, bgcolor: "#F35A3A", boxShadow: "none", "&:hover": { bgcolor: "#E64A2A", boxShadow: "none" } }}
              >
                {creating ? "Creando tu negocio..." : "Crear mi negocio"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
}

export default function ProjectsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const superAdmin = isSuperAdmin(user);
  const {
    projects,
    activeProject,
    projectCreation,
    setActiveProject,
    createProject,
    loadProjects,
    loading,
    error,
  } = useProjects();
  const [form, setForm] = React.useState(() => emptyForm(
    user?.email || "",
    "plus",
  ));
  const [branding, setBranding] = React.useState(emptyBranding);
  const [localError, setLocalError] = React.useState("");
  const [seoMigrationBusy, setSeoMigrationBusy] = React.useState(false);
  const [seoMigrationMessage, setSeoMigrationMessage] = React.useState("");
  const [seoMigrationError, setSeoMigrationError] = React.useState("");
  const [creating, setCreating] = React.useState(false);
  const [createStatus, setCreateStatus] = React.useState("");
  const [requestedProjects, setRequestedProjects] = React.useState(false);
  const compactProjectCards = useMediaQuery("(max-width:899.95px)");

  const ownedCount = Number(projectCreation?.ownedCount ?? (superAdmin ? 0 : projects.length));
  const canCreateProject = superAdmin || Boolean(
    projectCreation?.canCreate ?? (!superAdmin && projects.length === 0),
  );
  const showCreatePanel = superAdmin || canCreateProject;
  const creationReason = String(projectCreation?.reason || "");
  const multiProjectSource = String(projectCreation?.multiProjectSource || "");
  const subscriptionTarget = projectCreation?.subscriptionProject || activeProject || projects[0] || null;
  const firstProjectMode = !superAdmin && projects.length === 0 && canCreateProject;

  React.useEffect(() => {
    let mounted = true;
    loadProjects().finally(() => {
      if (mounted) setRequestedProjects(true);
    });
    return () => {
      mounted = false;
    };
  }, [loadProjects]);

  React.useEffect(() => {
    setForm((previous) => ({
      ...previous,
      ownerEmail: previous.ownerEmail || user?.email || "",
    }));
  }, [user]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((previous) => {
      const next = { ...previous, [name]: value };
      if (name === "name") next.slug = makeSlug(value);
      return next;
    });
  };

  const handleBrandingChange = (key, file) => {
    setBranding((previous) => ({ ...previous, [key]: file }));
  };

  const handleSubmit = async (event, guidedSetup = null) => {
    event.preventDefault();
    let project = null;

    try {
      setCreating(true);
      setCreateStatus(firstProjectMode ? "Creando tu proyecto..." : "Creando el proyecto y su espacio privado...");
      setLocalError("");

      const createPayload = {
        name: form.name,
        slug: makeSlug(form.slug || form.name),
        ownerEmail: form.ownerEmail,
        plan: form.plan,
        businessProfile: form.businessProfile,
        domains: form.domains,
        modules: modulesForPlan(form.plan),
      };

      if (firstProjectMode) {
        const businessGoals = Array.isArray(guidedSetup?.businessGoals)
          ? guidedSetup.businessGoals
          : form.businessGoals;
        createPayload.businessGoals = businessGoals;
        createPayload.capabilities = guidedSetup?.capabilities || capabilitiesForBusinessGoals(
          form.businessProfile,
          businessGoals,
        );
      }

      project = await createProject(createPayload);

      setCreateStatus(firstProjectMode ? "Proyecto creado. Estamos preparando tu espacio..." : "Proyecto creado. Render está preparando la página pública...");

      const hasBranding = Boolean(branding.logo || branding.compactLogo || branding.favicon);
      if (hasBranding && project?.slug) {
        setCreateStatus("Subiendo tus logos y recursos visuales...");
        await uploadInitialBranding(project.slug, branding, form.name);
      }

      setForm(emptyForm(user?.email || "", "plus"));
      setBranding(emptyBranding);

      if (project?.slug) {
        // UX-2026: land inside the new project's own Inicio, not the global
        // dashboard redirect. Existing multi-project admin flow stays as-is.
        navigate(firstProjectMode
          ? `/dashboard/proyectos/${encodeURIComponent(project.slug)}/inicio`
          : `/dashboard/proyectos/${project.slug}/pagina`, {
          state: firstProjectMode
            ? { onboardingMessage: "Tu negocio ya está creado. Podés configurarlo paso a paso." }
            : undefined,
        });
      }
    } catch (err) {
      if (project?.slug) {
        setLocalError(
          `El proyecto ${project.name || project.slug} fue creado, pero no se pudo completar la identidad visual: ${err.message || "error desconocido"}. Podés reintentar desde Landing > Ajustes > Recursos visuales.`
        );
        await loadProjects();
      } else {
        setLocalError(err.message || "No se pudo crear el proyecto");
      }
    } finally {
      setCreating(false);
      setCreateStatus("");
    }
  };

  const handleMigratePendingSeo = async () => {
    if (!superAdmin || seoMigrationBusy) return;
    setSeoMigrationError("");
    setSeoMigrationMessage("");
    setSeoMigrationBusy(true);

    try {
      const previewData = await projectsApi.inspectPendingStorefrontGatewayMigrations({
        previewLimit: 12,
      });
      const inspection = previewData?.inspection || {};
      const pendingCount = Number(inspection.pendingCount || 0);
      const domainCount = Number(inspection.domainCount || 0);

      if (!pendingCount) {
        setSeoMigrationMessage("Todas las páginas con dominio ya usan el gateway SEO correcto.");
        return;
      }

      const accepted = window.confirm(
        `Se encontraron ${pendingCount} proyecto(s) y ${domainCount} dominio(s) pendientes de migrar al SEO nuevo. ` +
        "La migración se hace por proyecto y conserva el rollback por dominio. ¿Continuar?",
      );
      if (!accepted) return;

      let afterId = "";
      let done = false;
      let migrated = 0;
      let failed = 0;
      let processed = 0;
      let rounds = 0;

      while (!done && rounds < 200) {
        const response = await projectsApi.migratePendingStorefrontGatewayBatch({
          afterId,
          limit: 8,
        });
        const batch = response?.migration || {};
        const batchProcessed = Number(batch.processed || 0);
        migrated += Number(batch.migrated || 0);
        failed += Number(batch.failed || 0);
        processed += batchProcessed;
        done = batch.done === true;
        const nextAfterId = String(batch.nextAfterId || "").trim();

        setSeoMigrationMessage(
          `Migrando SEO: ${processed} procesados · ${migrated} correctos · ${failed} con error...`,
        );

        if (done || !nextAfterId || nextAfterId === afterId) break;
        afterId = nextAfterId;
        rounds += 1;
      }

      const finalData = await projectsApi.inspectPendingStorefrontGatewayMigrations({
        previewLimit: 12,
      });
      const remaining = Number(finalData?.inspection?.pendingCount || 0);
      await loadProjects();

      if (remaining > 0) {
        setSeoMigrationError(
          `Se migraron ${migrated} proyecto(s), pero quedaron ${remaining} pendientes. ` +
          "Los que fallaron conservaron su servicio anterior y se pueden reintentar.",
        );
        setSeoMigrationMessage("");
      } else {
        setSeoMigrationMessage(
          `Listo: ${migrated} proyecto(s) quedaron en el gateway SEO. No quedan migraciones pendientes.`,
        );
      }
    } catch (error) {
      setSeoMigrationError(error?.message || "No se pudo completar la migración SEO masiva.");
      setSeoMigrationMessage("");
    } finally {
      setSeoMigrationBusy(false);
    }
  };

  if ((!requestedProjects || loading) && !projects.length && !projectCreation) {
    return <DashboardLoadingState label="Cargando proyectos…" minHeight={420} />;
  }

  if (firstProjectMode) {
    return (
      <FirstProjectOnboarding
        form={form}
        creating={creating}
        createStatus={createStatus}
        error={localError || error}
        onChange={handleChange}
        onSubmit={handleSubmit}
      />
    );
  }

  return (
    <Stack spacing={{ xs: 2, md: 3 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.4}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 950, color: BRAND.dark, overflowWrap: "anywhere" }}>
            Proyectos
          </Typography>
          <Typography sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.65)", mt: 0.5 }}>
            {superAdmin
              ? "Creá clientes y administrá sus proyectos."
              : "Elegí un proyecto o agregá uno nuevo cuando tu cuenta lo permita."}
          </Typography>
        </Box>
        {superAdmin ? (
          <Button
            variant="outlined"
            startIcon={seoMigrationBusy ? <WebProfitSpinner size={17} /> : <SyncRoundedIcon />}
            disabled={seoMigrationBusy}
            onClick={handleMigratePendingSeo}
            sx={{
              alignSelf: { xs: "stretch", sm: "center" },
              minHeight: 42,
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 900,
              borderColor: "rgba(105,87,232,.36)",
              color: BRAND.purple,
            }}
          >
            {seoMigrationBusy ? "Migrando páginas…" : "Migrar SEO pendientes"}
          </Button>
        ) : null}
      </Stack>

      {(error || localError) && <Alert severity="error" onClose={() => setLocalError("")}>{localError || error}</Alert>}
      {seoMigrationError ? <Alert severity="warning" onClose={() => setSeoMigrationError("")}>{seoMigrationError}</Alert> : null}
      {seoMigrationMessage ? <Alert severity="success" onClose={() => setSeoMigrationMessage("")}>{seoMigrationMessage}</Alert> : null}

      {!superAdmin && !canCreateProject && creationReason === "subscription_required" ? (
        <Paper elevation={0} sx={{ p: { xs: 1.6, sm: 2.5 }, borderRadius: 4, border: "1px solid rgba(243,90,58,.22)", bgcolor: "rgba(243,90,58,.045)" }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} justifyContent="space-between">
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box sx={{ width: 42, height: 42, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.12)", color: BRAND.orange, flexShrink: 0 }}>
                <WorkspacePremiumRoundedIcon />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
                  Tu primer proyecto ya está creado
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.35, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.66)", maxWidth: 720 }}>
                  Para agregar un segundo proyecto necesitás activar una suscripción en tu proyecto actual. La suscripción habilita un proyecto adicional sin modificar el que ya tenés.
                </Typography>
              </Box>
            </Stack>
            <Button
              variant="contained"
              disabled={!subscriptionTarget?.slug}
              onClick={() => subscriptionTarget?.slug && navigate(`/dashboard/proyectos/${subscriptionTarget.slug}/configuracion/suscripcion`)}
              sx={{
                width: { xs: "100%", sm: "auto" },
                minHeight: 42,
                bgcolor: BRAND.orange,
                borderRadius: { xs: 2.2, sm: 999 },
                textTransform: "none",
                fontWeight: 900,
                px: 2.5,
                flexShrink: 0,
              }}
            >
              Activar suscripción
            </Button>
          </Stack>
        </Paper>
      ) : null}

      {!superAdmin && !canCreateProject && creationReason === "limit_reached" ? (
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          Tu cuenta ya utiliza los {projectCreation?.maxProjects || 2} proyectos disponibles: el primero incluido y un proyecto adicional habilitado por tu prueba de 30 días o por una suscripción activa.
        </Alert>
      ) : null}

      <Grid container spacing={3}>
        {showCreatePanel && (
          <Grid item xs={12} md={5}>
            <Paper elevation={0} sx={{ p: { xs: 1.6, sm: 2.25, md: 3 }, borderRadius: 4, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),0.08)" }}>
              <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: { xs: 1.5, md: 2.5 } }}>
                <Box sx={{ width: 44, height: 44, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,0.1)", color: BRAND.orange }}>
                  <AddRoundedIcon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark, overflowWrap: "anywhere" }}>
                    {superAdmin ? "Nuevo cliente" : "Agregar otro proyecto"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.62)" }}>
                    {superAdmin
                      ? "Completá lo esencial; lo técnico puede quedar para después."
                      : multiProjectSource === "trial"
                        ? "Tu prueba completa de 30 días habilita un proyecto adicional para esta cuenta."
                        : "Tu suscripción habilitó un proyecto adicional para esta cuenta."}
                  </Typography>
                </Box>
              </Stack>

              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={{ xs: 1.4, sm: 2 }}>
                  <TextField label="Nombre del negocio" name="name" value={form.name} onChange={handleChange} required fullWidth />
                  <TextField
                    select
                    label="Tipo de negocio (opcional)"
                    name="businessProfile"
                    value={form.businessProfile}
                    onChange={handleChange}
                    helperText="Sólo ordena las sugerencias iniciales."
                    fullWidth
                  >
                    <MenuItem value="">Configurar después</MenuItem>
                    {PROJECT_BUSINESS_PROFILES.map((profile) => (
                      <MenuItem key={profile.key} value={profile.key}>{profile.label}</MenuItem>
                    ))}
                  </TextField>
                  {superAdmin ? (
                    <TextField label="Email owner" name="ownerEmail" type="email" value={form.ownerEmail} onChange={handleChange} required fullWidth />
                  ) : null}
                  <Paper variant="outlined" sx={{ p: 1.35, borderRadius: 2.5, bgcolor: "rgba(38,162,105,.045)", borderColor: "rgba(38,162,105,.22)" }}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" sx={{ color: BRAND.green, fontWeight: 1000 }}>
                          PRUEBA INICIAL AUTOMÁTICA
                        </Typography>
                        <Typography sx={{ mt: 0.2, fontWeight: 950, color: BRAND.dark }}>
                          Sistema completo durante 30 días
                        </Typography>
                      </Box>
                      <ContextualHelp
                        compact
                        title="Qué incluye la prueba"
                        text="El proyecto empieza con todas las herramientas principales habilitadas."
                        bullets={[
                          "Landing, catálogo, pedidos y cobros.",
                          "Sistema interno, estadísticas y Web Push.",
                          "Al finalizar, el cliente elige qué servicios conservar.",
                        ]}
                      />
                    </Stack>
                  </Paper>

                  <Box
                    component="details"
                    {...(!compactProjectCards ? { open: true } : {})}
                    sx={{
                      border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)",
                      borderRadius: 2.6,
                      bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.015)",
                      "&[open] summary": { borderBottom: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.07)" },
                    }}
                  >
                    <Box
                      component="summary"
                      sx={{
                        px: 1.35,
                        py: 1.15,
                        cursor: "pointer",
                        color: BRAND.dark,
                        fontWeight: 900,
                        fontSize: ".86rem",
                        "&::marker": { color: BRAND.orange },
                      }}
                    >
                      Dirección, dominio e identidad
                    </Box>
                    <Stack spacing={1.4} sx={{ p: 1.35 }}>
                      <TextField
                        label="Dirección inicial"
                        name="slug"
                        value={form.slug}
                        onChange={handleChange}
                        required
                        fullWidth
                        helperText="Se genera desde el nombre; cambiala sólo si necesitás otra."
                      />
                      <TextField
                        label="Dominio público"
                        name="domains"
                        value={form.domains}
                        onChange={handleChange}
                        fullWidth
                        multiline
                        minRows={2}
                        helperText="Opcional. Uno por línea o separados por coma."
                      />

                      <Divider />
                      <Box>
                        <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Identidad inicial</Typography>
                        <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", mt: 0.2 }}>
                          Opcional; también se puede completar después.
                        </Typography>
                      </Box>
                      <PendingAssetField
                        label="Logo principal"
                        file={branding.logo}
                        disabled={creating}
                        helperText="SVG, PNG, WebP o JPG."
                        onChange={(file) => handleBrandingChange("logo", file)}
                      />
                      <PendingAssetField
                        label="Logo compacto"
                        file={branding.compactLogo}
                        disabled={creating}
                        helperText="Versión reducida para móvil."
                        onChange={(file) => handleBrandingChange("compactLogo", file)}
                      />
                      <PendingAssetField
                        label="Favicon"
                        file={branding.favicon}
                        disabled={creating}
                        accept="image/svg+xml,image/png,image/webp,image/x-icon,.svg,.ico"
                        helperText="Preferentemente cuadrado."
                        onChange={(file) => handleBrandingChange("favicon", file)}
                      />
                    </Stack>
                  </Box>

                  {createStatus ? <Alert severity="info">{createStatus}</Alert> : null}
                  <Button
                    type="submit"
                    disabled={creating}
                    variant="contained"
                    size="large"
                    sx={{
                      minHeight: 44,
                      bgcolor: BRAND.orange,
                      borderRadius: { xs: 2.2, sm: 999 },
                      textTransform: "none",
                      fontWeight: 900,
                    }}
                  >
                    {creating
                      ? "Preparando proyecto..."
                      : superAdmin
                        ? "Crear proyecto"
                        : ownedCount === 0
                          ? "Crear mi primer proyecto"
                          : "Agregar proyecto"}
                  </Button>
                </Stack>
              </Box>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12} md={showCreatePanel ? 7 : 12}>
          <Stack spacing={2}>
            {loading && projects.length ? <DashboardLoadingState label="Actualizando proyectos…" minHeight={160} /> : null}
            {projects.map((projectItem) => {
              const isActive = activeProject?.slug === projectItem.slug;
              const modules = normalizeProjectModules(projectItem.modules);
              const moduleLabels = activeModuleLabels(projectItem);
              const memberAccess = normalizeProjectAccess(projectItem.memberAccess);
              const projectNavigation = resolveProjectNavigation({
                project: projectItem,
                memberAccess,
                superAdmin,
              });
              const landingAccess = projectNavigation.toolsByKey.landing;
              const productsAccess = projectNavigation.toolsByKey.products;
              const ordersAccess = projectNavigation.toolsByKey.orders;
              const planAccess = projectNavigation.toolsByKey.settings?.secondaryTools?.find(
                (tool) => tool.key === "settings-suscripcion",
              );
              const projectUsable = superAdmin || !memberAccess.resolved || memberAccess.status === "active";
              const canRetryDeployment = superAdmin || hasProjectRole(memberAccess, ["owner", "admin"]);
              const planLabel = BILLING_PLAN_LABELS[
                normalizeProjectPlanCode(projectItem.billing?.planCode || projectItem.plan)
              ];
              return (
                <Card
                  key={projectItem.id || projectItem.slug}
                  elevation={0}
                  sx={{
                    minWidth: 0,
                    borderRadius: 4,
                    border: `1px solid ${isActive ? BRAND.orange : "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.08)"}`,
                  }}
                >
                  <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                    <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={{ xs: 1.5, md: 2 }}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap" useFlexGap>
                          <Typography
                            variant="h5"
                            sx={{ minWidth: 0, maxWidth: "100%", fontWeight: 950, color: BRAND.dark, overflowWrap: "anywhere" }}
                          >
                            {projectItem.name}
                          </Typography>
                          {isActive && <Chip label="Activo" size="small" sx={{ bgcolor: "rgba(243,90,58,0.12)", color: BRAND.orange, fontWeight: 900 }} />}
                        </Stack>

                        {compactProjectCards ? (
                          <Stack direction="row" spacing={.65} useFlexGap flexWrap="wrap" sx={{ mt: .8 }}>
                            <Chip size="small" label={planLabel || "Plan"} variant="outlined" sx={{ fontWeight: 850 }} />
                            <Chip
                              size="small"
                              label={projectItem.billing?.status || "trial"}
                              color={["active", "trial", "exempt"].includes(projectItem.billing?.status || "trial") ? "success" : "warning"}
                              sx={{ fontWeight: 850 }}
                            />
                          </Stack>
                        ) : (
                          <>
                            <Typography sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.62)", mt: 0.75, overflowWrap: "anywhere" }}>
                              /{projectItem.slug} · DB: {projectItem.dbName} · Plan: {planLabel}
                            </Typography>
                            {projectItem.domains?.length ? (
                              <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", mt: 0.45, overflowWrap: "anywhere" }}>
                                Dominios asociados: {projectItem.domains.join(", ")}
                              </Typography>
                            ) : null}
                          </>
                        )}

                        <Box sx={{ mt: { xs: 1, md: 1.5 }, maxWidth: 760 }}>
                          {projectUsable ? (
                            <ProjectDeploymentCard project={projectItem} compact canRetry={canRetryDeployment} />
                          ) : (
                            <Alert severity="warning">Tu membresía en este proyecto está suspendida.</Alert>
                          )}
                        </Box>

                        {compactProjectCards ? (
                          <Box
                            component="details"
                            sx={{
                              mt: 1,
                              borderTop: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.07)",
                              "&[open] summary": { mb: .8 },
                            }}
                          >
                            <Box
                              component="summary"
                              sx={{
                                pt: .9,
                                cursor: "pointer",
                                color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)",
                                fontSize: ".78rem",
                                fontWeight: 900,
                                "&::marker": { color: BRAND.orange },
                              }}
                            >
                              Detalles del proyecto
                            </Box>
                            <Stack spacing={.55} sx={{ minWidth: 0 }}>
                              <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", overflowWrap: "anywhere" }}>
                                Dirección: /{projectItem.slug}
                              </Typography>
                              {projectItem.dbName ? (
                                <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", overflowWrap: "anywhere" }}>
                                  Base: {projectItem.dbName}
                                </Typography>
                              ) : null}
                              {projectItem.domains?.length ? (
                                <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", overflowWrap: "anywhere" }}>
                                  Dominios: {projectItem.domains.join(", ")}
                                </Typography>
                              ) : null}
                              <Stack direction="row" spacing={.6} useFlexGap flexWrap="wrap" sx={{ pt: .2 }}>
                                <Chip size="small" label={`Página: ${projectItem.status || "active"}`} variant="outlined" />
                                {moduleLabels.length > 0 ? moduleLabels.map((label) => (
                                  <Chip key={label} size="small" label={label} variant="outlined" />
                                )) : <Chip size="small" label="Sin módulos" />}
                              </Stack>
                            </Stack>
                          </Box>
                        ) : (
                          <>
                            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                              <Chip size="small" label={`Billing: ${projectItem.billing?.status || "trial"}`} color={["active", "trial", "exempt"].includes(projectItem.billing?.status || "trial") ? "success" : "warning"} />
                              <Chip size="small" label={`Página: ${projectItem.status || "active"}`} variant="outlined" />
                            </Stack>
                            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
                              {moduleLabels.length > 0 ? moduleLabels.map((label) => (
                                <Chip key={label} size="small" label={label} variant="outlined" />
                              )) : <Chip size="small" label="Sin módulos" />}
                            </Stack>
                          </>
                        )}
                      </Box>
                      {compactProjectCards ? (
                        <Stack spacing={.8} sx={{ width: "100%", maxWidth: { md: 220 }, flexShrink: 0 }}>
                          <Button
                            fullWidth
                            variant={isActive ? "contained" : "outlined"}
                            disabled={!projectUsable}
                            onClick={() => setActiveProject(projectItem)}
                            sx={{
                              minHeight: 42,
                              borderRadius: 2.2,
                              textTransform: "none",
                              fontWeight: 900,
                              bgcolor: isActive ? BRAND.orange : "transparent",
                              borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.18)",
                            }}
                          >
                            {projectUsable ? (isActive ? "Proyecto activo" : "Usar proyecto") : "Acceso suspendido"}
                          </Button>
                          <ProjectActionsMenu
                            project={projectItem}
                            superAdmin={superAdmin}
                            showPlan={Boolean(planAccess?.visible)}
                            showLanding={Boolean(modules.landing && landingAccess?.visible)}
                            showProducts={Boolean(modules.products && productsAccess?.visible)}
                            showOrders={Boolean(modules.orders && ordersAccess?.visible)}
                            onNavigate={navigate}
                          />
                        </Stack>
                      ) : (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
                          <Button variant={isActive ? "contained" : "outlined"} disabled={!projectUsable} onClick={() => setActiveProject(projectItem)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, bgcolor: isActive ? BRAND.orange : "transparent", borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.18)" }}>
                            {projectUsable ? "Usar" : "Acceso suspendido"}
                          </Button>
                          {superAdmin && (
                            <Button onClick={() => navigate(`/dashboard/proyectos/${projectItem.slug}/configuracion/general`)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, color: BRAND.dark }}>
                              Administrar
                            </Button>
                          )}
                          {planAccess?.visible ? (
                            <Button onClick={() => navigate(`/dashboard/proyectos/${projectItem.slug}/configuracion/suscripcion`)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, color: BRAND.orange }}>
                              Plan
                            </Button>
                          ) : null}
                          {modules.landing && landingAccess?.visible ? (
                            <Button endIcon={<ArrowForwardRoundedIcon />} onClick={() => navigate(`/dashboard/proyectos/${projectItem.slug}/pagina`)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, color: BRAND.dark }}>
                              Landing
                            </Button>
                          ) : null}
                          {modules.products && productsAccess?.visible ? (
                            <Button endIcon={<ArrowForwardRoundedIcon />} onClick={() => navigate(`/dashboard/proyectos/${projectItem.slug}/productos`)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, color: BRAND.dark }}>
                              Productos
                            </Button>
                          ) : null}
                          {modules.orders && ordersAccess?.visible ? (
                            <Button endIcon={<ArrowForwardRoundedIcon />} onClick={() => navigate(`/dashboard/proyectos/${projectItem.slug}/pedidos`)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850, color: BRAND.dark }}>
                              Pedidos
                            </Button>
                          ) : null}
                        </Stack>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
}
